import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ArrowLeft, RefreshCw, CheckCircle2, AlertTriangle,
  Info, Layers, ArrowUpCircle, ShieldCheck,
} from "lucide-react";

/**
 * Read-only Automation Mapping Matrix — Phase 1 of the intent-scoped
 * mapping architecture.
 *
 * Shows the canonical intent → current template → compatible templates →
 * suggested upgrade grid. Never writes. Never mutates. When the user has
 * signed off that the matrix is correct, Phase 2 wires the intent-filtered
 * picker + save validation on top of the same data.
 *
 * Route: /app/whatsapp/automations/matrix
 */
export default function AutomationMappingMatrix() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/whatsapp/automations/mapping-matrix");
      setData(r.data);
    } catch (e) {
      toast.error(apiError(e, "Failed to load mapping matrix"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  if (loading && !data) return <div className="p-8 text-sm text-ink-mid">Loading…</div>;
  if (!data) return null;

  const rows = data.matrix || [];
  const valid   = rows.filter(r => r.current_valid === true).length;
  const invalid = rows.filter(r => r.current_valid === false).length;
  const unmapped = rows.filter(r => !r.current_template).length;
  const upgrades = rows.filter(r => r.suggested_upgrade).length;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-6 py-8 space-y-6" data-testid="automation-matrix-page">
      <div>
        <div className="flex items-center justify-between gap-4">
          <Link to="/app/whatsapp/automations" className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink"
                data-testid="matrix-back">
            <ArrowLeft className="w-3.5 h-3.5" /> Back to Automations
          </Link>
          <button onClick={load} className="inline-flex items-center gap-2 text-xs text-ink-mid hover:text-ink"
                  data-testid="matrix-reload">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Reload
          </button>
        </div>
        <div className="mt-3">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink-mid mb-1">
            <ShieldCheck className="w-3 h-3" /> Read-only · Phase 1
          </div>
          <h1 className="font-display text-3xl text-ink">Automation Mapping Matrix</h1>
          <p className="mt-1 text-sm text-ink-mid max-w-3xl">
            The single source of truth for which Meta template is compatible with which automation.
            Each automation has a <strong>canonical intent</strong>; each template resolves to exactly one
            intent via a deterministic registry. Cross-intent picks are rejected. This page is <strong>read-only</strong> —
            no writes happen from here. Once the matrix is confirmed correct, Phase 2 wires the intent-filtered picker.
          </p>
        </div>
        <div className="mt-4 flex items-center gap-3 text-[11px] flex-wrap">
          <StatChip tint="emerald" testid="matrix-stat-valid">{valid} valid</StatChip>
          <StatChip tint="rose" testid="matrix-stat-invalid">{invalid} invalid</StatChip>
          <StatChip tint="amber" testid="matrix-stat-unmapped">{unmapped} unmapped</StatChip>
          <StatChip tint="violet" testid="matrix-stat-upgrades">{upgrades} upgrades available</StatChip>
          <StatChip tint="stone" testid="matrix-stat-approved">{data.approved_count} approved WABA templates</StatChip>
        </div>
      </div>

      {!data.clinic_connected && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900" data-testid="matrix-not-connected">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>Meta Cloud API is not connected for this clinic.
              <Link to="/app/whatsapp/connect-meta" className="underline ml-1">Connect first →</Link>
            </div>
          </div>
        </div>
      )}

      {data.template_fetch_error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-900" data-testid="matrix-fetch-error">
          Could not fetch approved templates: <code className="text-[11px]">{JSON.stringify(data.template_fetch_error)}</code>
        </div>
      )}

      <div className="rounded-2xl border border-[#E7E1D3] bg-white overflow-hidden">
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest text-ink-mid font-medium bg-linen/60 border-b border-[#E7E1D3] px-4 py-3">
          <div className="col-span-3">Automation intent</div>
          <div className="col-span-3">Current template</div>
          <div className="col-span-4">Compatible templates</div>
          <div className="col-span-2">Suggested upgrade</div>
        </div>
        <div>
          {rows.map(r => <MatrixRow key={r.intent} r={r} />)}
        </div>
      </div>

      {/* Orphan templates — approved on WABA but not in the intent registry */}
      {data.orphan_templates && data.orphan_templates.length > 0 && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/40 p-4 space-y-2" data-testid="matrix-orphans">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-amber-900 font-medium">
            <Info className="w-3 h-3" /> Orphan templates ({data.orphan_templates.length})
          </div>
          <p className="text-xs text-ink-mid">
            These templates are approved on your WABA but have no canonical intent yet. Add them to the intent registry
            (backend <code className="text-[10px]">whatsapp_intents.EXPLICIT_TEMPLATE_INTENT</code>) so they become pickable.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.orphan_templates.map(o => (
              <span key={o.name} className="text-[11px] font-mono px-2 py-1 rounded-full bg-white border border-amber-200 text-amber-900"
                    data-testid={`matrix-orphan-${o.name}`}>
                {o.name} · {o.language} · {o.category}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="text-[10px] text-ink-mid font-mono">
        Generated: {data.generated_at} · Graph API: {data.graph_version}
      </div>
    </div>
  );
}


/* ────────────────────────────  Matrix Row ──────────────────────────── */
function MatrixRow({ r }) {
  const isValid   = r.current_valid === true;
  const isInvalid = r.current_valid === false;
  const isEmpty   = r.current_template == null;

  const stateTint = isValid ? "border-emerald-100 bg-emerald-50/20"
                  : isInvalid ? "border-rose-100 bg-rose-50/30"
                  : "border-[#E7E1D3] bg-white";

  return (
    <div className={`grid grid-cols-12 gap-3 px-4 py-4 border-b last:border-0 ${stateTint} border-l-2 ${
      isValid ? "border-l-emerald-500" : isInvalid ? "border-l-rose-500" : "border-l-stone-300"
    }`} data-testid={`matrix-row-${r.intent}`}>

      {/* Col 1 — Intent */}
      <div className="col-span-3 min-w-0">
        <div className="text-sm font-medium text-ink truncate">{r.label}</div>
        <code className="text-[10px] text-ink-mid font-mono">{r.intent}</code>
        <p className="mt-1 text-[11px] text-ink-mid line-clamp-2">{r.trigger}</p>
        <div className="mt-2 flex flex-wrap gap-1">
          {(r.expected_params || []).map(p => (
            <span key={p} className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-stone-100 text-ink-mid border border-stone-200">
              {p}
            </span>
          ))}
          {(r.expected_buttons || []).map(b => (
            <span key={b} className="text-[9px] font-mono px-1.5 py-0.5 rounded-full bg-indigo-50 text-indigo-800 border border-indigo-200">
              btn:{b}
            </span>
          ))}
        </div>
        {!r.legacy_automation_key && (
          <div className="mt-2 text-[10px] text-amber-900 inline-flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" /> New intent — no automation wired yet
          </div>
        )}
      </div>

      {/* Col 2 — Current template */}
      <div className="col-span-3 min-w-0">
        {isEmpty ? (
          <div className="text-xs text-ink-mid italic" data-testid={`matrix-current-${r.intent}-empty`}>
            No template configured
          </div>
        ) : (
          <div>
            <div className="text-sm font-mono text-ink truncate" data-testid={`matrix-current-${r.intent}`}>
              {r.current_template}
            </div>
            {isValid && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
                <CheckCircle2 className="w-3 h-3" /> Valid · resolves to {r.current_template_intent}
              </div>
            )}
            {isInvalid && (
              <div className="mt-1 inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-rose-50 border border-rose-200 text-rose-800"
                   data-testid={`matrix-invalid-${r.intent}`}>
                <AlertTriangle className="w-3 h-3" />
                Mismatch · resolves to {r.current_template_intent || "unknown"}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Col 3 — Compatible templates */}
      <div className="col-span-4 min-w-0">
        {r.compatible_templates.length === 0 ? (
          <div className="text-xs text-ink-mid italic" data-testid={`matrix-compat-${r.intent}-empty`}>
            No compatible templates on WABA yet. Create/approve one for intent
            <code className="ml-1 text-[10px] font-mono">{r.intent}</code>.
          </div>
        ) : (
          <div className="flex flex-wrap gap-1.5" data-testid={`matrix-compat-${r.intent}`}>
            {r.compatible_templates.map(t => (
              <span
                key={t.name}
                className={`text-[10px] font-mono px-2 py-1 rounded-full border inline-flex items-center gap-1 ${
                  t.is_current
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-violet-50 border-violet-200 text-violet-800"
                }`}
                data-testid={`matrix-compat-${r.intent}-${t.name}`}>
                {t.is_current
                  ? <CheckCircle2 className="w-3 h-3" />
                  : <ArrowUpCircle className="w-3 h-3" />}
                {t.name}
                <span className="text-[9px] opacity-70">· {t.language}</span>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Col 4 — Suggested upgrade */}
      <div className="col-span-2 min-w-0">
        {r.suggested_upgrade ? (
          <div className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-violet-50 border border-violet-200 text-violet-800 font-mono"
               data-testid={`matrix-suggested-${r.intent}`}>
            <Layers className="w-3 h-3" /> {r.suggested_upgrade}
          </div>
        ) : (
          <div className="text-[11px] text-ink-mid italic">—</div>
        )}
      </div>
    </div>
  );
}


function StatChip({ children, tint = "stone", testid }) {
  const tints = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-800",
    rose:    "bg-rose-50 border-rose-200 text-rose-800",
    amber:   "bg-amber-50 border-amber-200 text-amber-900",
    violet:  "bg-violet-50 border-violet-200 text-violet-800",
    stone:   "bg-stone-100 border-stone-200 text-ink-mid",
  };
  return (
    <span className={`px-3 py-1 rounded-full border ${tints[tint]}`} data-testid={testid}>
      {children}
    </span>
  );
}
